import axios from "axios";
import { setupCache } from "axios-cache-adapter";

import config from "../utils/config";
import store from "../redux-saga/store";
import { navigateTarget, showErrorMessage } from "../redux-saga/redux/toast";
import { resetMyProfileState } from "../redux-saga/redux/profile/myProfile";
import { resetTransactionState } from "../redux-saga/redux/profile/transactions";
import { resetAuthenticationState } from "../redux-saga/redux/auth/authentication";
import { resetPaymentState } from "../redux-saga/redux/payment";
import { getTranslation } from "../utils/helper";
import Session from "../utils/session";
import { LANGUAGE_CODE } from "../utils/constants";
import { updateMaintenanceStatus } from "../redux-saga/redux/settings";
import UserLocation from "../utils/UserLocation";

export const updateAppStatus = ({
  casinoShutdown,
  sportsbookShutdown,
  totalShutdown
}) => {
  if (
    casinoShutdown !== null ||
    sportsbookShutdown !== null ||
    totalShutdown !== null
  ) {
    if (
      UserLocation.casinoShutdown !== casinoShutdown ||
      UserLocation.sportsbookShutdown !== sportsbookShutdown ||
      UserLocation.totalShutdown !== totalShutdown
    ) {
      store.dispatch(
        updateMaintenanceStatus({
          casinoShutdown,
          sportsbookShutdown,
          totalShutdown
        })
      );
    }
  }

  UserLocation.updateAppStatus = {
    casinoShutdown,
    sportsbookShutdown,
    totalShutdown
  };
};
export const cache = setupCache({
  maxAge: 60 * 1000 * 60,
  exclude: {
    query: false
  },
  clearOnStale: true,
  clearOnError: true,
  invalidate: async (config, request) => {
    if (request.clearCacheEntry) {
      await config.store.removeItem(config.uuid);
    }
  }
});
const axiosClient = axios.create({
  baseURL: config.baseUrl,
  headers: {
    "Content-Type": "application/json",
    "domain-key": config.domainKey
  },
  maxConcurrentRequests: 10,
  adapter: cache.adapter
});

export const axiosClientNotCached = axios.create({
  baseURL: config.baseUrl,
  headers: {
    "Content-Type": "application/json",
    "domain-key": config.domainKey
  },
  maxConcurrentRequests: 10
});

axiosClientNotCached.interceptors.response.use(
  (res) => res,
  (err) => {
    if (!window.navigator.onLine) {
      throw new axios.Cancel(getTranslation("LOBBY.CONNECT_INTERNET"));
    }

    if (err?.response) {
      const originalRequest = err.config;
      if (
        err.response?.status === 401 &&
        originalRequest?.url === "/user/refreshToken" &&
        originalRequest?.url !== "/user/login"
      ) {
        store.dispatch(showErrorMessage({ tKey: "HEADER.SESSION_TIMEOUT" }));
        Session.clearSessionCred();
        store.dispatch(navigateTarget("/"));
        store.dispatch(resetMyProfileState());
        store.dispatch(resetTransactionState());
        store.dispatch(resetAuthenticationState());
        store.dispatch(resetPaymentState());
        return { data: "" };
      }

      if (
        err.response.status === 401 &&
        !originalRequest._retry &&
        originalRequest?.url !== "/user/login"
      ) {
        originalRequest._retry = true;
        return axiosClient
          .get("/user/refreshToken")
          .then(({ data }) => {
            if (data) {
              const { token, resToken } = data;
              const { userId } = Session.getSessionCred;
              Session.saveSessionCred({
                token,
                resToken,
                userId
              });
              // Change Authorization header
              axiosClient.defaults.headers.common["access-token"] = token;
              axiosClient.defaults.headers.common["refresh-token"] = resToken;
              // return originalRequest object with Axios.
              return axiosClient(originalRequest);
            } else {
              return Promise.reject(err?.response);
            }
          })
          .catch((refreshError) => {
            return Promise.reject(refreshError);
          });
      }

      if (err?.response?.data) {
        const {
          casinoShutdown = null,
          sportsbookShutdown = null,
          totalShutdown = null
        } = err.response.data;
        updateAppStatus({
          casinoShutdown,
          sportsbookShutdown,
          totalShutdown
        });
      }

      return Promise.reject(err.response);
    }
    return Promise.reject(err);
  }
);

axiosClientNotCached.interceptors.request.use((req) => {
  if (!navigator.onLine) {
    throw new axios.Cancel(getTranslation("LOBBY.CONNECT_INTERNET"));
  }
  const tokens = Session.getSessionCred;
  req.headers["language"] =
    LANGUAGE_CODE?.[Session.getLanguage] || config.defaultLanguage;
  if (tokens) {
    req.headers["access-token"] = tokens.token;
    req.headers["refresh-token"] = tokens.resToken;
  }
  return req;
});

axiosClient.interceptors.response.use(
  (res) => res,
  (err) => {
    if (!window.navigator.onLine) {
      throw new axios.Cancel(getTranslation("LOBBY.CONNECT_INTERNET"));
    }
    if (err?.response) {
      const originalRequest = err.config;

      if (
        err.response?.status === 401 &&
        originalRequest?.url === "/user/refreshToken" &&
        originalRequest?.url !== "/user/login"
      ) {
        store.dispatch(showErrorMessage({ tKey: "HEADER.SESSION_TIMEOUT" }));
        Session.clearSessionCred();
        store.dispatch(navigateTarget("/"));
        store.dispatch(resetMyProfileState());
        store.dispatch(resetTransactionState());
        store.dispatch(resetAuthenticationState());
        store.dispatch(resetPaymentState());
        return { data: "" };
      }

      if (
        err.response.status === 401 &&
        !originalRequest._retry &&
        originalRequest?.url !== "/user/login"
      ) {
        originalRequest._retry = true;
        return axiosClient
          .get("/user/refreshToken")
          .then(({ data }) => {
            if (data) {
              const { token, resToken } = data;
              const { userId } = Session.getSessionCred;
              Session.saveSessionCred({
                token,
                resToken,
                userId
              });
              // Change Authorization header
              axiosClient.defaults.headers.common["access-token"] = token;
              axiosClient.defaults.headers.common["refresh-token"] = resToken;
              // return originalRequest object with Axios.
              return axiosClient(originalRequest);
            } else {
              return Promise.reject(err?.response);
            }
          })
          .catch((refreshError) => {
            return Promise.reject(refreshError);
          });
      }

      if (err?.response?.data) {
        const {
          casinoShutdown = null,
          sportsbookShutdown = null,
          totalShutdown = null
        } = err.response.data;
        updateAppStatus({
          casinoShutdown,
          sportsbookShutdown,
          totalShutdown
        });
      }

      return Promise.reject(err.response);
    }
    return Promise.reject(err);
  }
);

axiosClient.interceptors.request.use((req) => {
  if (!navigator.onLine) {
    throw new axios.Cancel(getTranslation("LOBBY.CONNECT_INTERNET"));
  }
  const tokens = Session.getSessionCred;
  req.headers["language"] =
    LANGUAGE_CODE?.[Session.getLanguage] || config.defaultLanguage;
  if (tokens) {
    req.headers["access-token"] = tokens.token;
    req.headers["refresh-token"] = tokens.resToken;
  }
  return req;
});

export default axiosClient;
